import { IDataTableColumn, showError, useGridState } from "nsitools-react";
import * as React from "react";

import { DrupalDataTable } from ".";
import { FileDownload, FileDto, ShpDocumentDto } from "../../api";
import { SharepointFileButton } from "../../components";
import { useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { b64toBlob } from "../../utils";
import { DrupalFieldSet } from "./DurpalFieldSet";
import { saveAs } from "file-saver";

export interface IDrupalSharepointFilesListProps {
  files: ShpDocumentDto[];
  loading: boolean;
  downloadFunc: (fileName: string) => Promise<FileDto>;
}

export const DrupalSharepointFilesList: React.FunctionComponent<IDrupalSharepointFilesListProps> = ({
  files,
  loading,
  downloadFunc,
}) => {
  const { t } = useTl();
  const tableState = useGridState<ShpDocumentDto>({
    serverMode: false,
    enablePagination: false,
    enableFilter: true,
  });

  const [filesLoading, setfilesLoading] = React.useState<string[]>([]);

  const downloadFile = React.useCallback(
    async (fileName: string) => {
      filesLoading.push(fileName);
      setfilesLoading([...filesLoading]);
      if (fileName) {
        try {
          var file = await downloadFunc(fileName);
          var blob = b64toBlob(new FileDownload({ base64Content: file.fileBytes, fileName: file.fileName }));
          saveAs(blob, file.fileName);
        } catch {
          showError(t(ETLCodes.Error));
        }
      }
      setfilesLoading([...filesLoading.filter((c) => c !== fileName)]);
    },
    [downloadFunc, filesLoading, t]
  );

  const { setData } = tableState;

  React.useEffect(() => {
    if (files) {
      setData(files);
    }
  }, [files, setData]);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.FileName),
        fieldName: "fileName",
        render: (row: ShpDocumentDto) => (
          <SharepointFileButton
            text={row.fileName}
            onClick={() => downloadFile(row.fileName)}
            loading={!!filesLoading.find((c) => c === row.fileName)}
            tooltip={ETLCodes.DownloadDocument}
          />
        ),
      },
      {
        header: () => t(ETLCodes.DocumentType),
        fieldName: "docType",
      },
      {
        header: () => t(ETLCodes.Date),
        fieldName: "date",
      },
    ],
    [downloadFile, filesLoading, t]
  );

  return (
    <DrupalFieldSet title={t(ETLCodes.Files)}>
      <DrupalDataTable tableState={tableState} columns={columns} loading={loading} />
    </DrupalFieldSet>
  );
};
